/**
 * Modal de **gestión de permisos granulares** (override por usuario).
 *
 * Réplica de `manage_permissions.html` del SSR (`progress/explore_equipo_ssr.md`,
 * §B.5/C.4): checkboxes categorizados, badge "Rol" en los permisos del rol base,
 * "Guardar permisos" (`PATCH /users/<username>/permissions`) y "Resetear a rol por
 * defecto" (`POST .../permissions/reset`).
 *
 * Modelo de permisos (rol vs override): el override del usuario sale de
 * `GET /users/<username>` (`permissions`, `[]` si usa los del rol); los del rol
 * base, de `GET /roles/<role_id>`. El estado inicial de los checkboxes marca el
 * override si lo hay; si no, hereda los del rol (igual que el SSR cuando no hay
 * personalización). Controlado por `username` del padre (`null` = cerrado).
 */
import { useEffect, useMemo, useState } from "react";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui";

import { groupPermissions, teamErrorMessage } from "../format";
import {
  useResetUserPermissions,
  useSetUserPermissions,
} from "../hooks/useUserMutations";
import { usePermissions, useRole, useUser } from "../hooks/useUsers";
import { PermissionsCheckboxGroup } from "./PermissionsCheckboxGroup";

interface ManagePermissionsDialogProps {
  /** Usuario cuyos permisos se gestionan; `null` mantiene el modal cerrado. */
  username: string | null;
  onClose: () => void;
}

export function ManagePermissionsDialog({
  username,
  onClose,
}: ManagePermissionsDialogProps) {
  const userQuery = useUser(username);
  const permissionsQuery = usePermissions();
  // El rol base se consulta una vez conocido el rol del usuario.
  const roleId = userQuery.data?.role ?? null;
  const roleQuery = useRole(roleId);
  const setPermissions = useSetUserPermissions();
  const resetPermissions = useResetUserPermissions();

  const [selected, setSelected] = useState<Set<string>>(new Set());

  const roleBase = useMemo(
    () => new Set(roleQuery.data?.permissions ?? []),
    [roleQuery.data],
  );

  const categories = useMemo(
    () => groupPermissions(permissionsQuery.data?.items ?? []),
    [permissionsQuery.data],
  );

  const hasOverride = userQuery.data?.has_custom_permissions ?? false;

  // Hidrata la selección inicial: el override si existe; si no, los del rol base
  // (espejo del SSR, donde sin personalización los checkboxes marcan el rol).
  useEffect(() => {
    if (username === null || userQuery.data === undefined) {
      return;
    }
    if (userQuery.data.has_custom_permissions) {
      setSelected(new Set(userQuery.data.permissions));
    } else {
      setSelected(new Set(roleQuery.data?.permissions ?? []));
    }
    setPermissions.reset();
    resetPermissions.reset();
    // Reacciona a la carga del usuario y del rol; las mutaciones son estables.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, userQuery.data, roleQuery.data]);

  function handleSave() {
    if (username === null) {
      return;
    }
    setPermissions.mutate(
      { username, body: { permissions: Array.from(selected) } },
      { onSuccess: onClose },
    );
  }

  function handleReset() {
    if (username === null) {
      return;
    }
    resetPermissions.mutate(username, { onSuccess: onClose });
  }

  const isLoading =
    userQuery.isPending || permissionsQuery.isPending || roleQuery.isPending;
  const loadError = userQuery.isError
    ? userQuery.error
    : permissionsQuery.isError
      ? permissionsQuery.error
      : null;
  const apiError = setPermissions.isError
    ? teamErrorMessage(setPermissions.error)
    : resetPermissions.isError
      ? teamErrorMessage(resetPermissions.error)
      : null;
  const isBusy = setPermissions.isPending || resetPermissions.isPending;

  return (
    <Dialog
      open={username !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogTitle>Gestionar permisos</DialogTitle>
        <DialogDescription>
          Usuario: {username ?? ""}
          {roleId ? ` · Rol base: ${roleId}` : ""}
          {hasOverride
            ? " · Permisos personalizados activos"
            : " · Usando permisos del rol"}
        </DialogDescription>

        <div className="mt-4">
          {isLoading ? (
            <p className="py-8 text-center text-sm text-slate-500" role="status">
              Cargando permisos…
            </p>
          ) : loadError ? (
            <p className="py-8 text-center text-sm text-red-600" role="alert">
              {teamErrorMessage(loadError)}
            </p>
          ) : (
            <PermissionsCheckboxGroup
              categories={categories}
              selected={selected}
              roleBasePermissions={roleBase}
              onChange={setSelected}
              idPrefix="perm"
            />
          )}
        </div>

        {apiError && (
          <p role="alert" className="mt-4 text-sm text-red-600">
            {apiError}
          </p>
        )}

        <div className="mt-6 flex flex-wrap justify-between gap-2">
          <Button
            type="button"
            variant="danger"
            onClick={handleReset}
            disabled={isBusy || isLoading || !hasOverride}
          >
            Resetear a rol por defecto
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={isBusy}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleSave}
              disabled={isBusy || isLoading || loadError !== null}
            >
              {setPermissions.isPending ? "Guardando…" : "Guardar permisos"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
